import { useCallback, useEffect, useState } from "react";

import { getApi } from "../../../api/api";
import {
  FullAddress,
  FullAddressInterface,
} from "../../../common/types/interfaces";

const emptyAddress: FullAddress = {
  name: "",
  description: "",
  isGeneral: false,
  entrance: "",
  floor: "",
  apartment: "",
  intercom: "",
  city: { value: "", latitude: "", longitude: "", isDirty: false },
  address: { value: "", latitude: "", longitude: "", isDirty: false },
};

export const useAddress = () => {
  const api = getApi();
  const [addresses, setAddresses] = useState<FullAddressInterface[]>([]);
  const [ready, setReady] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<FullAddressInterface | null>(null);
  const [saving, setSaving] = useState(false);

  const getList = useCallback(async () => {
    try {
      const result = await api.getAddresses();
      setAddresses(result || []);
    } catch {
      setAddresses([]);
    } finally {
      setReady(true);
    }
  }, [api]);

  useEffect(() => {
    getList();
  }, [getList]);

  const startAdding = useCallback(() => {
    setEditing(null);
    setAdding(true);
  }, []);

  const startEditing = useCallback((address: FullAddressInterface) => {
    setEditing(address);
    setAdding(true);
  }, []);

  const closeForm = useCallback(() => {
    setAdding(false);
    setEditing(null);
  }, []);

  const initialData: FullAddress = editing
    ? {
        name: editing.name || "",
        description: editing.description || "",
        isGeneral: editing.isGeneral,
        entrance: editing.entrance || "",
        floor: editing.floor || "",
        apartment: editing.apartment || "",
        intercom: editing.intercom || "",
        city: {
          value: editing.city?.value || "",
          latitude: editing.city?.latitude ?? "",
          longitude: editing.city?.longitude ?? "",
          isDirty: true,
        },
        address: {
          value: editing.address?.value || "",
          latitude: editing.address?.latitude ?? "",
          longitude: editing.address?.longitude ?? "",
          isDirty: true,
        },
      }
    : emptyAddress;

  const createAddress = useCallback(
    async (data: FullAddress) => {
      setSaving(true);
      try {
        await api.createAddress(data);
        await getList();
        closeForm();
      } finally {
        setSaving(false);
      }
    },
    [api, getList, closeForm],
  );

  const updateAddress = useCallback(
    async (data: FullAddress) => {
      if (!editing?.id) return;
      setSaving(true);
      try {
        await api.createAddress({ ...data, isGeneral: !!editing.isGeneral });
        await api.deleteAddress(editing.id);
        await getList();
        closeForm();
      } finally {
        setSaving(false);
      }
    },
    [api, getList, closeForm, editing],
  );

  const deleteAddress = useCallback(
    async (id: number) => {
      await api.deleteAddress(id);
      await getList();
    },
    [api, getList],
  );

  const setDefaultAddress = useCallback(
    async (id: number) => {
      await api.setDefaultAddress(id);
      await getList();
    },
    [api, getList],
  );

  const isEmpty = ready && addresses.length === 0 && !adding;

  return {
    addresses,
    ready,
    adding,
    saving,
    editing,
    initialData,
    isEmpty,
    startAdding,
    startEditing,
    closeForm,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  };
};